import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import DataTable, { fmtCurrency, fmtDate } from "../components/DataTable";
import { apiCall } from "../auth/AuthContext";
import { Crown, Medal, Award } from "lucide-react";

export default function AdminReferrals() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const d = await apiCall("/api/admin/referrals"); setRecords(d.records || []); }
      catch { setRecords([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const podium = useMemo(() => {
    const sorted = [...records].sort((a, b) => (b["Total Referrals"] || 0) - (a["Total Referrals"] || 0));
    return sorted.slice(0, 3);
  }, [records]);

  const columns = [
    { key: "Referral Code", label: "Code" },
    { key: "Code Owner", label: "Owner", sortable: true },
    { key: "Owner Type", label: "Type" },
    { key: "Total Referrals", label: "Referrals", sortable: true },
    { key: "Reward Per Referral", label: "Reward Each", fmt: fmtCurrency },
    { key: "Rewards Paid", label: "Paid", fmt: fmtCurrency },
    { key: "Date Created", label: "Since", fmt: fmtDate },
  ];

  const podiumStyle = [
    { Icon: Crown, color: "text-jc-gold", border: "border-jc-gold", scale: "md:scale-110" },
    { Icon: Medal, color: "text-jc-gold-light", border: "border-jc-gold-light/70", scale: "" },
    { Icon: Award, color: "text-amber-700", border: "border-amber-700/70", scale: "" },
  ];

  return (
    <AdminLayout title="REFERRAL LEADERBOARD" kicker="Top Referrers">
      {/* Podium */}
      <div className="mb-10 grid sm:grid-cols-3 gap-4 md:gap-6">
        {podium.length > 0 ? podium.map((r, i) => {
          const s = podiumStyle[i];
          return (
            <div key={r.id} data-testid={`podium-${i + 1}`} className={`relative p-6 border ${s.border} bg-jc-black/40 backdrop-blur-sm ${s.scale} transition-transform`}>
              <div className="flex items-center justify-between mb-3">
                <s.Icon size={28} className={s.color} />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">#{i + 1}</span>
              </div>
              <div className="font-display text-2xl text-jc-white tracking-wide truncate">{r["Code Owner"] || "—"}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-jc-gold">{r["Referral Code"] || ""}</div>
              <div className="mt-4 flex items-end gap-2">
                <div className="font-display text-5xl text-jc-gold leading-none">{r["Total Referrals"] || 0}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted pb-2">referrals</div>
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-jc-muted">{fmtCurrency(r["Rewards Paid"] || 0)} paid</div>
            </div>
          );
        }) : (
          <div className="sm:col-span-3 p-10 border border-jc-gold/20 text-center font-mono text-xs uppercase tracking-[0.25em] text-jc-muted">No referrals yet — your podium awaits.</div>
        )}
      </div>

      <DataTable
        testId="admin-referrals-table"
        loading={loading}
        records={records}
        columns={columns}
        searchKeys={["Referral Code", "Code Owner", "Owner Type"]}
        emptyText="No referral codes yet."
        initialSort={{ key: "Total Referrals", dir: "desc" }}
      />
    </AdminLayout>
  );
}
